import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatSpecialistUserLabel } from "../../app/helpers/flowCaseMassHelper";
import { FLOW_MODULE_DEFINITIONS } from "../../app/helpers/flowEngineConfigHelper";

const PROCESS_BY_MODULE_ID = Object.fromEntries(
  Object.entries(FLOW_MODULE_DEFINITIONS).map(([process, def]) => [Number(def.moduleId), process])
);

const ADVANCED_FILTER_KEYS = [
  "catalogciius_id",
  "catalogserie_id",
  "state_catalog_id",
  "current_role_catalog_id",
  "flow_coverage_group_id",
  "case_phase"
];

const ALL_CLEAR_KEYS = [
  "process",
  "text",
  "current_user_id",
  "from",
  "to",
  "case_phase",
  "case_phase_group",
  ...ADVANCED_FILTER_KEYS
];

const isFilled = (value) => value != null && String(value).trim() !== "";

const isFacetAvailable = (availableFacets, facetKey, value) => {
  if (!availableFacets || !facetKey) return true;
  if (value == null || String(value).trim() === "") return true;
  const list = availableFacets[facetKey];
  if (!Array.isArray(list)) return true;
  return list.some((item) => String(item) === String(value));
};

const FilterFieldLabel = ({
  label,
  active = false,
  showMarkers = false,
  onClear,
  t
}) => (
  <label className="form-label d-flex align-items-center gap-1 mb-1">
    <span>{label}</span>
    {showMarkers && active && (
      <>
        <i
          className="fa fa-filter text-danger"
          title={t("flow.cmi.filters.active", "Este filtro está aplicado")}
          aria-hidden="true"
        />
        <button
          type="button"
          className="btn btn-link p-0 text-danger lh-1"
          title={t("flow.cmi.filters.clear-field", "Quitar este filtro")}
          aria-label={t("flow.cmi.filters.clear-field", "Quitar este filtro")}
          onClick={onClear}
        >
          <i className="fa fa-times-circle" />
        </button>
      </>
    )}
  </label>
);

FilterFieldLabel.propTypes = {
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
  showMarkers: PropTypes.bool,
  onClear: PropTypes.func,
  t: PropTypes.func.isRequired
};

const FlowCaseFilterBar = ({
  filters,
  onChange,
  onSearch,
  loading = false,
  modules = [],
  states = [],
  catalogRoles = [],
  users = [],
  ciius = [],
  series = [],
  coverageGroups = [],
  showModuleFilter = true,
  showTrayFilter = true,
  showUserFilter = true,
  userFirstRow = false,
  advancedOpen = false,
  onToggleAdvanced,
  showSeriesFilter = true,
  showPhaseFilter = false,
  phaseOptions = [],
  phaseInAdvanced = false,
  showClearFilters = false,
  showFilterMarkers = false,
  availableFacets = null,
  onClearFilters,
  onClearField
}) => {
  const { t } = useTranslation();

  const patch = (field, value) => {
    onChange({ ...filters, [field]: value });
  };

  const clearField = (field) => {
    if (onClearField) {
      onClearField(field);
      return;
    }
    patch(field, "");
  };

  const advancedActive = ADVANCED_FILTER_KEYS.some((key) => {
    if (key === "current_role_catalog_id" && !showTrayFilter) {
      return false;
    }
    if (key === "catalogserie_id" && !showSeriesFilter) {
      return false;
    }
    if (key === "case_phase" && !showPhaseFilter) {
      return false;
    }
    return isFilled(filters[key]);
  });

  const hasActiveFilters = ALL_CLEAR_KEYS.some((key) => isFilled(filters[key]));

  const optionDisabled = (facetKey, value) => {
    if (!availableFacets || !Object.prototype.hasOwnProperty.call(availableFacets, facetKey)) {
      return false;
    }
    if (value == null || String(value).trim() === "") {
      return false;
    }
    if (String(filters[facetKey] || "") === String(value)) {
      return false;
    }
    return !isFacetAvailable(availableFacets, facetKey, value);
  };

  const optionTitle = (facetKey, value) =>
    optionDisabled(facetKey, value)
      ? t(
          "flow.cmi.filters.not-in-universe",
          "No hay casos con este valor en el universo actual de la bandeja"
        )
      : undefined;

  const labelCoverageGroup = (group) => {
    const name = group.name || group.code || group.id;
    if (filters.process || !group.catalogmodule_id) {
      return name;
    }
    const process = PROCESS_BY_MODULE_ID[Number(group.catalogmodule_id)];
    const moduleLabel = process ? t(`modules.${process}`, process) : group.catalogmodule_id;
    return `${moduleLabel} — ${name}`;
  };

  const renderPhaseSelect = (colClass) => (
    <div className={colClass}>
      <FilterFieldLabel
        label={t("flow.cmi.phase-group", "Fase")}
        active={isFilled(filters.case_phase)}
        showMarkers={showFilterMarkers}
        onClear={() => clearField("case_phase")}
        t={t}
      />
      <select
        className={`form-select${isFilled(filters.case_phase) && showFilterMarkers ? " border-danger" : ""}`}
        value={filters.case_phase || ""}
        onChange={(event) => patch("case_phase", event.target.value)}
      >
        <option value="">{t("flow.cmi.all", "Todos")}</option>
        {phaseOptions.map((item) => (
          <option
            key={item.value}
            value={item.value}
            disabled={optionDisabled("case_phase", item.value)}
            title={optionTitle("case_phase", item.value)}
          >
            {t(item.key, item.fallback)}
          </option>
        ))}
      </select>
    </div>
  );

  const showFirstRowPhase = showPhaseFilter && phaseOptions.length > 0 && !phaseInAdvanced;
  const showAdvancedPhase = showPhaseFilter && phaseOptions.length > 0 && phaseInAdvanced;

  return (
    <>
      <form className="row g-2 align-items-end mb-2" onSubmit={onSearch}>
        {showModuleFilter && (
          <div className="col-md-2">
            <FilterFieldLabel
              label={t("flow.cmi.module", "Módulo")}
              active={isFilled(filters.process)}
              showMarkers={showFilterMarkers}
              onClear={() => clearField("process")}
              t={t}
            />
            <select
              className={`form-select${isFilled(filters.process) && showFilterMarkers ? " border-danger" : ""}`}
              value={filters.process || ""}
              onChange={(event) => patch("process", event.target.value)}
            >
              {modules.map((item) => (
                <option
                  key={item.key || "all"}
                  value={item.key}
                  disabled={optionDisabled("process", item.key)}
                  title={optionTitle("process", item.key)}
                >
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={showModuleFilter ? (userFirstRow ? "col-md-2" : "col-md-3") : "col-md-4"}>
          <FilterFieldLabel
            label={t("flow.cmi.text", "Texto")}
            active={isFilled(filters.text)}
            showMarkers={showFilterMarkers}
            onClear={() => clearField("text")}
            t={t}
          />
          <input
            className={`form-control${isFilled(filters.text) && showFilterMarkers ? " border-danger" : ""}`}
            value={filters.text || ""}
            onChange={(event) => patch("text", event.target.value)}
          />
        </div>
        {showUserFilter && userFirstRow && (
          <div className="col-md-2">
            <FilterFieldLabel
              label={t("flow.cmi.user", "Usuario")}
              active={isFilled(filters.current_user_id)}
              showMarkers={showFilterMarkers}
              onClear={() => clearField("current_user_id")}
              t={t}
            />
            <select
              className={`form-select${isFilled(filters.current_user_id) && showFilterMarkers ? " border-danger" : ""}`}
              value={filters.current_user_id || ""}
              onChange={(event) => patch("current_user_id", event.target.value)}
            >
              <option value="">{t("flow.cmi.all", "Todos")}</option>
              {users.map((user) => (
                <option
                  key={user.id}
                  value={user.id}
                  disabled={optionDisabled("current_user_id", user.id)}
                  title={optionTitle("current_user_id", user.id)}
                >
                  {formatSpecialistUserLabel(user)}
                </option>
              ))}
            </select>
          </div>
        )}
        {showFirstRowPhase && renderPhaseSelect("col-md-2")}
        <div className="col-md-2">
          <FilterFieldLabel
            label={t("flow.cmi.from", "Desde")}
            active={isFilled(filters.from)}
            showMarkers={showFilterMarkers}
            onClear={() => clearField("from")}
            t={t}
          />
          <input
            type="date"
            className={`form-control${isFilled(filters.from) && showFilterMarkers ? " border-danger" : ""}`}
            value={filters.from || ""}
            onChange={(event) => patch("from", event.target.value)}
          />
        </div>
        <div className="col-md-2">
          <FilterFieldLabel
            label={t("flow.cmi.to", "Hasta")}
            active={isFilled(filters.to)}
            showMarkers={showFilterMarkers}
            onClear={() => clearField("to")}
            t={t}
          />
          <input
            type="date"
            className={`form-control${isFilled(filters.to) && showFilterMarkers ? " border-danger" : ""}`}
            value={filters.to || ""}
            onChange={(event) => patch("to", event.target.value)}
          />
        </div>
        <div className="col-lg-auto ms-lg-auto">
          <div className="d-flex flex-nowrap gap-2 justify-content-end">
            <button type="submit" className="btn btn-primary flex-shrink-0" disabled={loading}>
              {t("buttons.search", "Buscar")}
            </button>
            <button
              type="button"
              className={`${advancedActive ? "btn btn-outline-danger" : "btn btn-outline-secondary"} flex-shrink-0`}
              onClick={onToggleAdvanced}
            >
              {advancedActive && <i className="fa fa-filter me-1" aria-hidden="true" />}
              {advancedOpen
                ? t("flow.cmi.filters.hide", "Ocultar filtros")
                : t("flow.cmi.filters.show", "Más filtros")}
            </button>
            {showClearFilters && (
              <button
                type="button"
                className="btn btn-outline-danger flex-shrink-0"
                disabled={!hasActiveFilters || loading}
                onClick={onClearFilters}
              >
                <i className="fa fa-times-circle me-1" aria-hidden="true" />
                {t("flow.cmi.filters.clear-all", "Eliminar filtros")}
              </button>
            )}
          </div>
        </div>
      </form>

      {advancedOpen && (
        <div className="row g-2 align-items-end mb-3 p-3 border rounded bg-light">
          {showAdvancedPhase && renderPhaseSelect("col-md-3")}
          <div className="col-md-3">
            <FilterFieldLabel
              label={t("flow.cmi.ciiu", "CIIU")}
              active={isFilled(filters.catalogciius_id)}
              showMarkers={showFilterMarkers}
              onClear={() => clearField("catalogciius_id")}
              t={t}
            />
            <select
              className={`form-select${isFilled(filters.catalogciius_id) && showFilterMarkers ? " border-danger" : ""}`}
              value={filters.catalogciius_id || ""}
              onChange={(event) => patch("catalogciius_id", event.target.value)}
            >
              <option value="">{t("flow.cmi.all", "Todos")}</option>
                {ciius.map((item) => (
                  <option
                    key={item.id}
                    value={item.id}
                    disabled={optionDisabled("catalogciius_id", item.id)}
                    title={optionTitle("catalogciius_id", item.id)}
                  >
                    {item.code ? `${item.code} — ${item.name}` : item.name}
                  </option>
                ))}
            </select>
          </div>
          {showSeriesFilter && (
          <div className="col-md-3">
            <FilterFieldLabel
              label={t("flow.cmi.series", "Serie")}
              active={isFilled(filters.catalogserie_id)}
              showMarkers={showFilterMarkers}
              onClear={() => clearField("catalogserie_id")}
              t={t}
            />
            <select
              className={`form-select${isFilled(filters.catalogserie_id) && showFilterMarkers ? " border-danger" : ""}`}
              value={filters.catalogserie_id || ""}
              onChange={(event) => patch("catalogserie_id", event.target.value)}
            >
              <option value="">{t("flow.cmi.all", "Todos")}</option>
              {series.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                  disabled={optionDisabled("catalogserie_id", item.id)}
                  title={optionTitle("catalogserie_id", item.id)}
                >
                  {item.name || item.description || item.id}
                </option>
              ))}
            </select>
          </div>
          )}
          <div className="col-md-3">
            <FilterFieldLabel
              label={t("flow.cmi.state", "Estado")}
              active={isFilled(filters.state_catalog_id)}
              showMarkers={showFilterMarkers}
              onClear={() => clearField("state_catalog_id")}
              t={t}
            />
            <select
              className={`form-select${isFilled(filters.state_catalog_id) && showFilterMarkers ? " border-danger" : ""}`}
              value={filters.state_catalog_id || ""}
              onChange={(event) => patch("state_catalog_id", event.target.value)}
            >
              <option value="">{t("flow.cmi.all", "Todos")}</option>
              {states.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                  disabled={optionDisabled("state_catalog_id", item.id)}
                  title={optionTitle("state_catalog_id", item.id)}
                >
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          {showTrayFilter && (
            <div className="col-md-3">
              <FilterFieldLabel
                label={t("flow.cmi.tray", "Bandeja")}
                active={isFilled(filters.current_role_catalog_id)}
                showMarkers={showFilterMarkers}
                onClear={() => clearField("current_role_catalog_id")}
                t={t}
              />
              <select
                className={`form-select${isFilled(filters.current_role_catalog_id) && showFilterMarkers ? " border-danger" : ""}`}
                value={filters.current_role_catalog_id || ""}
                onChange={(event) => patch("current_role_catalog_id", event.target.value)}
              >
                <option value="">{t("flow.cmi.all", "Todas")}</option>
                {catalogRoles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="col-md-3">
            <FilterFieldLabel
              label={t("flow.cmi.coverageGroup", "Agrupador (Bloque)")}
              active={isFilled(filters.flow_coverage_group_id)}
              showMarkers={showFilterMarkers}
              onClear={() => clearField("flow_coverage_group_id")}
              t={t}
            />
            <select
              className={`form-select${isFilled(filters.flow_coverage_group_id) && showFilterMarkers ? " border-danger" : ""}`}
              value={filters.flow_coverage_group_id || ""}
              onChange={(event) => patch("flow_coverage_group_id", event.target.value)}
            >
              <option value="">{t("flow.cmi.all", "Todos")}</option>
              {coverageGroups.map((group) => (
                <option
                  key={group.id}
                  value={group.id}
                  disabled={optionDisabled("flow_coverage_group_id", group.id)}
                  title={optionTitle("flow_coverage_group_id", group.id)}
                >
                  {labelCoverageGroup(group)}
                </option>
              ))}
            </select>
          </div>
          {showUserFilter && !userFirstRow && (
            <div className="col-md-3">
              <FilterFieldLabel
                label={t("flow.cmi.user", "Usuario")}
                active={isFilled(filters.current_user_id)}
                showMarkers={showFilterMarkers}
                onClear={() => clearField("current_user_id")}
                t={t}
              />
              <select
                className={`form-select${isFilled(filters.current_user_id) && showFilterMarkers ? " border-danger" : ""}`}
                value={filters.current_user_id || ""}
                onChange={(event) => patch("current_user_id", event.target.value)}
              >
                <option value="">{t("flow.cmi.all", "Todos")}</option>
                {users.map((user) => (
                  <option
                    key={user.id}
                    value={user.id}
                    disabled={optionDisabled("current_user_id", user.id)}
                    title={optionTitle("current_user_id", user.id)}
                  >
                    {formatSpecialistUserLabel(user)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </>
  );
};

FlowCaseFilterBar.propTypes = {
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  modules: PropTypes.array,
  states: PropTypes.array,
  catalogRoles: PropTypes.array,
  users: PropTypes.array,
  ciius: PropTypes.array,
  series: PropTypes.array,
  coverageGroups: PropTypes.array,
  showModuleFilter: PropTypes.bool,
  showTrayFilter: PropTypes.bool,
  showUserFilter: PropTypes.bool,
  userFirstRow: PropTypes.bool,
  advancedOpen: PropTypes.bool,
  onToggleAdvanced: PropTypes.func,
  showSeriesFilter: PropTypes.bool,
  showPhaseFilter: PropTypes.bool,
  phaseOptions: PropTypes.array,
  phaseInAdvanced: PropTypes.bool,
  showClearFilters: PropTypes.bool,
  showFilterMarkers: PropTypes.bool,
  availableFacets: PropTypes.object,
  onClearFilters: PropTypes.func,
  onClearField: PropTypes.func
};

export default FlowCaseFilterBar;
